import { type ItemGroup } from 'types'
import React, { type FC, useEffect, useRef, useState } from "react";
import clsx from 'clsx'
import { type UpdateItemFormValues } from '../utils/types'
import { ManageGroupsMenu } from '../../ManageGroupsMenu/ManageGroupsMenu'
import { CancelConfirmation } from '../../Modals/CancelConfirmation/CancelConfirmation'
import { Tooltip } from "../../Tooltip/Tooltip";
import { DeleteConfirmation } from "../../Modals/DeleteConfirmation/DeleteConfirmation";

interface ItemGroupNavProps {
  itemGroups: ItemGroup[];
  onSelect: (itemGroup: UpdateItemFormValues)=> void;
  onAdd: (sid: string)=> void;
  onDelete: (itemGroup: Partial<ItemGroup>)=> void;
  onCancel: (itemGroup: Partial<ItemGroup>)=> void;
  disableDelete?: boolean;
  role?: string;
}

export const ItemGroupsNav: FC<ItemGroupNavProps> = ({ itemGroups, onSelect, onAdd, onDelete, onCancel, disableDelete , role = 'buyer'}) => {
  const [selected, setSelected] = useState<ItemGroup | null>(itemGroups[0]);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [itemGroupToCancel, setItemGroupToCancel] = useState<Partial<ItemGroup>|null>(null);
  const [itemGroupToDelete, setItemGroupToDelete] = useState<Partial<ItemGroup>|null>(null);

  const tooltipRef = useRef(null)
  const [cachedItemGroups, setCachedItemGroups] = useState<ItemGroup[]>(itemGroups);

  useEffect(()=>{
    if (itemGroups.length !== cachedItemGroups.length) {
      setCachedItemGroups(itemGroups);
      setSelected(itemGroups[itemGroups.length - 1])
    }
  }, [itemGroups])


  const handleSelect = (itemGroup: ItemGroup): void => {
    setSelected(itemGroup)

    const itemGroupToUpdate = itemGroup as unknown as UpdateItemFormValues
    onSelect?.(itemGroupToUpdate);
  }

  const handleAdd = (): void => {
    const newSid = `new-${itemGroups.length}`
    onAdd?.(newSid)
  }

  const handleCancel = (itemGroup: Partial<ItemGroup>): void => {
    setShowCancelConfirmation(true);
    setItemGroupToCancel(itemGroup);
  }


  const handleDelete = (itemGroup: Partial<ItemGroup>): void => {
    setShowDeleteConfirmation(true);
    setItemGroupToDelete(itemGroup);
  }


  const confirmCancel = (): void => {
    onCancel?.(itemGroupToCancel as Partial<ItemGroup>)
    setShowCancelConfirmation(false);
    setTimeout(()=>{
      setItemGroupToCancel(null);
    }, 500)
  }

  const confirmDelete = (): void => {
    onDelete?.(itemGroupToDelete as Partial<ItemGroup>)
    setShowDeleteConfirmation(false);
    setTimeout(()=>{
      setItemGroupToDelete(null);
    }, 500)
  }

  const handleNoCancelation = (): void => {
    setShowCancelConfirmation(false);
    setItemGroupToCancel(null);
  }

  const handleNoDelete = (): void => {
    setShowDeleteConfirmation(false);
    setItemGroupToDelete(null)
  }

  return (
    <>
      <div className="flex justify-between">
        <div className="flex gap-4">
          {itemGroups.map((itemGroup, index) => (
            <div key={itemGroup.sid} ref={index === 0 ? tooltipRef : null}>
              <button
                className={clsx('font-sans text-offBlack-dark border-b-2 border-transparent font-semibold text-sm', {
                  'border-b-teal-dark text-teal-dark': selected?.sid === itemGroup.sid,
                })}
                onClick={() => {
                handleSelect(itemGroup)
              }}>Shipment {index + 1}</button>
            </div>
          ))}
          <Tooltip position='bottom' ref={tooltipRef}>
            <p>
              Use shipments to split this line item
              <br/>
              and track resulting quantities separately
            </p>
          </Tooltip>
        </div>
        <ManageGroupsMenu
          itemGroup={selected as Partial<ItemGroup>}
          onAdd={handleAdd}
          onCancel={handleCancel}
          onDelete={handleDelete}
          disableDelete={disableDelete}
          role={role}
        />
      </div>
      {showCancelConfirmation ? <CancelConfirmation onClose={handleNoCancelation} onConfirm={confirmCancel} /> : null}
      {showDeleteConfirmation ? <DeleteConfirmation onClose={handleNoDelete} onConfirm={confirmDelete} /> : null}
    </>

  )
}
